/**
 * Shared Axios instance with credentials enabled.
 * 
 * This ensures all API calls include HttpOnly cookies (session_id).
 * NO tokens are stored in frontend - only session_id in HttpOnly cookie.
 */

import axios from "axios";
import { getApiBase } from "./apiConfig";
import { isPublicRoute } from "../constants/publicRoutes";

const API_BASE_URL = `${getApiBase()}/api`;

// CSRF token storage (in memory only - NOT localStorage/sessionStorage)
let csrfToken: string | null = null;

// Create axios instance with credentials enabled
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // CRITICAL: Include HttpOnly cookies in all requests
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000, // Default timeout: 60 seconds (can be overridden per request)
});

// Request interceptor: Add CSRF token to POST/PUT/DELETE/PATCH requests
apiClient.interceptors.request.use(
  async (config) => {
    // Only add CSRF token for state-changing methods
    if (config.method && ["post", "put", "delete", "patch"].includes(config.method.toLowerCase())) {
      // Fetch CSRF token if not already available
      if (!csrfToken) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/csrf-token`, {
            withCredentials: true,
          });
          csrfToken = response.data.csrf_token;
        } catch (error) {
          console.error("Failed to fetch CSRF token:", error);
        }
      }
      
      // Add CSRF token to header (cookie is automatically included)
      if (csrfToken) {
        config.headers["X-CSRF-Token"] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling auth errors and CSRF token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Update CSRF token if provided in response header
    const newCsrfToken = response.headers["x-csrf-token"];
    if (newCsrfToken) {
      csrfToken = newCsrfToken;
    }
    return response;
  },
  async (error) => {
    // Handle 401 Unauthorized - redirect to login
    // But only if we're not already on public/auth pages (prevents loops)
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isPublic = isPublicRoute(currentPath);
      
      // Check if we're already on login page with error params (prevents redirect loops)
      const urlParams = new URLSearchParams(window.location.search);
      const fromError = urlParams.get("from_error");
      
      // Don't redirect if:
      // 1. We're on public/auth pages (prevents loops and unnecessary redirects)
      // 2. We're already on login page with from_error param (prevents redirect loops)
      if (!isPublic && !fromError) {
        // Small delay to avoid immediate redirect after callback
        // Redirect to frontend login page (not backend API endpoint)
        setTimeout(() => {
          window.location.href = `/auth/login?return_url=${encodeURIComponent(window.location.href)}`;
        }, 100);
      }
      
      // Suppress 401 errors in console for public routes or login page with error params (expected behavior)
      if (isPublic || fromError) {
        // Create a silent error that won't be logged to console
        const silentError = new Error();
        silentError.name = "SilentAuthError";
        // Don't log to console for public routes or login page with error params
        return Promise.reject(silentError);
      }
    }
    // Handle 429 Too Many Requests (Rate Limit) - redirect to error page
    if (error.response?.status === 429) {
      const currentPath = window.location.pathname;
      // Don't redirect if already on error page or login page (prevents loops)
      if (!currentPath.includes("/auth/error") && !currentPath.includes("/auth/login")) {
        const detail = error.response?.data?.detail || "Rate limit exceeded";
        window.location.href = `/auth/error?error=rate_limit&detail=${encodeURIComponent(detail)}`;
        return Promise.reject(new Error("Rate limit exceeded"));
      }
      // If already on error/login page, just reject silently to prevent loops
      return Promise.reject(new Error("Rate limit exceeded"));
    }
    
    // Handle 403 Forbidden (CSRF error) - refresh CSRF token and retry
    if (error.response?.status === 403 && error.response?.data?.detail?.includes("CSRF")) {
      csrfToken = null; // Force refresh on next request
      // Retry the request once after refreshing CSRF token
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/csrf-token`, {
          withCredentials: true,
        });
        csrfToken = response.data.csrf_token;
        // Retry original request with new CSRF token
        if (csrfToken && error.config) {
          error.config.headers = error.config.headers || {};
          error.config.headers["X-CSRF-Token"] = csrfToken;
          return apiClient.request(error.config);
        }
      } catch (retryError) {
        console.error("Failed to refresh CSRF token for retry:", retryError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

